/* ==========================================================================
   LIVVY — IN-PAGE ANCHORS
   --------------------------------------------------------------------------
   A plain `href="#target"` jump is computed once, against the layout as it
   stands at that instant. On mobile the page below the fold is still settling
   — lazy images resolving, carousels measuring, app blocks injecting — so the
   target keeps moving down after the browser has already jumped. The visitor
   lands short, on whatever section now occupies that scroll position.

   Here the jump is re-applied once the layout stops moving: after images load
   and after a couple of animation frames, and again on `load`. Cheap, and it
   only ever runs for links that point at an element on the page.
   ========================================================================== */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const targetOf = (hash) => {
    if (!hash || hash === '#') return null;
    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch (e) {
      return null;
    }
  };

  const scrollTo = (el, smooth) => {
    el.scrollIntoView({
      behavior: smooth && !REDUCED.matches ? 'smooth' : 'auto',
      block: 'start',
    });
  };

  /* Re-apply the jump while the page is still settling. Each pass corrects for
     anything that grew above the target since the last one. */
  const settle = (el) => {
    let last = null;
    let tries = 0;

    const step = () => {
      const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
      if (top !== last) {
        last = top;
        scrollTo(el, false);
      }
      if (++tries < 12) setTimeout(step, 120);
    };

    requestAnimationFrame(step);
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || link.target === '_blank') return;

    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || url.host !== window.location.host) return;

    const el = targetOf(url.hash);
    if (!el) return;

    event.preventDefault();
    history.pushState(null, '', url.hash);
    scrollTo(el, true);
    settle(el);
  });

  /* Arriving with a hash already in the URL - e.g. the rating on the home page
     linking through to the product page's reviews. */
  const onArrive = () => {
    const el = targetOf(window.location.hash);
    if (el) settle(el);
  };

  if (document.readyState === 'complete') onArrive();
  else window.addEventListener('load', onArrive);
})();
